import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LoaderCircle, MailCheck, KeyRound, BadgeCheck, XCircle } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { formatApiError } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children, testid }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#09090B] px-4 text-white" data-testid={testid}>
    <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl">
      <Link to="/" className="flex items-center gap-2">
        <Logo className="h-9 w-9" />
        <span className="font-display text-lg font-extrabold tracking-tight">FUN<span className="text-[#06B6D4]">Z</span></span>
      </Link>
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#7C3AED]/60";
const btnCls = "glow-btn-b flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] py-4 font-display text-sm font-bold uppercase tracking-wider text-white disabled:opacity-60";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not send the email — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testid="forgot-page">
      {sent ? (
        <div className="text-center">
          <MailCheck className="mx-auto h-12 w-12 text-[#22C55E]" />
          <h1 className="mt-4 font-display text-2xl font-extrabold" data-testid="forgot-sent">Check your inbox</h1>
          <p className="mt-2 text-sm text-slate-400">
            If <span className="text-[#06B6D4]">{email}</span> has a FUNZ account, a reset link is on its way. It works for 30 minutes.
          </p>
          <Link to="/login" data-testid="forgot-back" className="mt-6 inline-block rounded-full border border-white/15 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-slate-200 hover:border-[#06B6D4]/60">
            Back to login
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <h1 className="font-display text-2xl font-extrabold">Forgot password?</h1>
            <p className="mt-1 text-sm text-slate-500">Enter your account email — we'll send you a reset link.</p>
          </div>
          <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@college.edu" className={inputCls} />
          {error && <p data-testid="forgot-error" className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/10 px-4 py-3 text-sm text-[#F97316]">{error}</p>}
          <button data-testid="forgot-submit" type="submit" disabled={busy} className={btnCls}>
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Send reset link"}
          </button>
          <p className="text-center text-xs text-slate-500">
            Remembered it? <Link to="/login" className="text-[#06B6D4] hover:underline">Log in</Link>
          </p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testid="reset-page">
      {done ? (
        <div className="text-center">
          <BadgeCheck className="mx-auto h-12 w-12 text-[#22C55E]" />
          <h1 className="mt-4 font-display text-2xl font-extrabold" data-testid="reset-done">Password updated</h1>
          <p className="mt-2 text-sm text-slate-400">Log in with your new password.</p>
          <Link to="/login" data-testid="reset-login" className={`${btnCls} mt-6 no-underline`}>Log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-2xl font-extrabold"><KeyRound className="h-6 w-6 text-[#06B6D4]" /> New password</h1>
            <p className="mt-1 text-sm text-slate-500">Pick a fresh one — 6+ characters.</p>
          </div>
          {!token && <p className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/10 px-4 py-3 text-sm text-[#F97316]">This link is missing its token — request a new reset email.</p>}
          <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className={inputCls} />
          <input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password" className={inputCls} />
          {error && <p data-testid="reset-error" className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/10 px-4 py-3 text-sm text-[#F97316]">{error}</p>}
          <button data-testid="reset-submit" type="submit" disabled={busy || !token} className={btnCls}>
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Update password"}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const token = params.get("token") || "";
    if (!token) return setState("fail");
    axios.get(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setState("ok"))
      .catch(() => setState("fail"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Shell testid="verify-page">
      <div className="text-center">
        {state === "checking" && <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-[#06B6D4]" />}
        {state === "ok" && (
          <>
            <BadgeCheck className="mx-auto h-12 w-12 text-[#22C55E]" />
            <h1 className="mt-4 font-display text-2xl font-extrabold" data-testid="verify-ok">Email verified</h1>
            <p className="mt-2 text-sm text-slate-400">You're all set — order updates, gig alerts and payout emails will reach you.</p>
          </>
        )}
        {state === "fail" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-[#F97316]" />
            <h1 className="mt-4 font-display text-2xl font-extrabold" data-testid="verify-fail">Link expired</h1>
            <p className="mt-2 text-sm text-slate-400">Request a fresh verification email from Settings in your dashboard.</p>
          </>
        )}
        <Link to="/app" data-testid="verify-continue" className={`${btnCls} mt-6 no-underline`}>Open my dashboard</Link>
      </div>
    </Shell>
  );
}
